import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Minus } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { Avatar } from './Avatar';
import { atendimentoApi } from '../modules/atendimento/lib/api';
import { useAlertaFaixa } from './alertas/AlertasContexto';

interface Parado {
  id: number;
  nome: string | null;
  telefone: string | null;
  jid: string;
  foto_url: string | null;
  atendente_nome: string | null;
  status_em: string;
  min_parado: number;
}

const POLLING_MS = 2 * 60 * 1000; // "aja agora" — refresca rapido (o ping no WhatsApp e o canal instantaneo)
const FUNDO = '#f97316';
const BORDA = '#c2410c';
const TEXTO = '#000';

// Chamados PARADOS agora (DA-114): encaminhado, rodizio esgotado (todas as atendentes
// disponiveis ja foram tentadas), cliente esperando sem resposta alem do limite.
// Regra unificada 1-ou-N. So admin/gerente. O scheduler tambem dispara ping no WhatsApp
// e modal em tempo real (chamado_parado_modal) — este banner e o aviso persistente.
export function AlertaParadosBanner() {
  const navigate = useNavigate();
  const { usuario } = useAuth();
  const [itens, setItens] = useState<Parado[]>([]);
  const [aberto, setAberto] = useState(false);

  const podeVer = usuario && ['admin', 'gerente'].includes(usuario.perfil);

  useEffect(() => {
    if (!podeVer) return;
    let cancelado = false;
    async function tick() {
      try {
        const r = await atendimentoApi.listarParados();
        if (!cancelado) setItens(r.itens);
      } catch { /* silencia */ }
    }
    void tick();
    const id = setInterval(tick, POLLING_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [podeVer]);

  const ativo = !!podeVer && itens.length > 0;
  const recolhido = useAlertaFaixa(
    'parados',
    ativo ? { rotulo: 'PARADO', bg: FUNDO, borda: BORDA, qtd: itens.length } : null,
  );

  if (!ativo || recolhido) return null;

  const maxMin = itens.reduce((m, p) => Math.max(m, p.min_parado), 0);

  return (
    <div style={{ background: FUNDO, borderLeft: `4px solid ${BORDA}`, color: TEXTO, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px' }}>
        <button
          type="button"
          onClick={() => setAberto((o) => !o)}
          title={aberto ? 'Recolher' : 'Ver chamados parados'}
          style={{
            background: 'rgba(0,0,0,0.18)', color: TEXTO, border: 'none', borderRadius: 6, cursor: 'pointer',
            width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
        >
          {aberto ? <Minus size={15} /> : <Plus size={15} />}
        </button>
        <span style={{ fontWeight: 700, letterSpacing: 0.3 }}>PARADO</span>
        <span>
          {itens.length} chamado{itens.length === 1 ? '' : 's'} sem resposta — rodizio esgotado, ha {maxMin} min. Assuma ou redirecione.
        </span>
      </div>

      {aberto && (
        <div style={{ padding: '0 14px 10px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Cartao e' <div role=button>, nao <button>: o navegador nao deixa selecionar texto
              dentro de <button>, e o telefone precisa ser copiavel com o mouse. Arrastar pra
              selecionar termina num click — com selecao ativa, o clique nao navega. */}
          {itens.map((p) => (
            <div
              key={p.id}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (window.getSelection()?.toString()) return;
                navigate('/atendimento');
              }}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate('/atendimento'); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                background: 'rgba(255,255,255,0.80)', border: `1px solid rgba(0,0,0,0.15)`,
                borderRadius: 6, padding: '6px 8px', cursor: 'pointer', color: '#000',
              }}
            >
              <Avatar nome={p.nome || p.telefone || '?'} fotoUrl={p.foto_url} tamanho={26} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {p.nome || p.telefone || p.jid} {p.telefone && p.nome ? '- ' : ''}
                  {p.telefone && p.nome && <span style={{ userSelect: 'text', cursor: 'text' }}>{p.telefone}</span>}
                </div>
                <div style={{ fontSize: 11 }}>
                  Atendente: <b>{p.atendente_nome ? p.atendente_nome.replace(/\s+FIODECOBRE$/i, '') : '—'}</b> · sem resposta ha {p.min_parado} min
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
